// ─────────────────────────────────────────────
// KLOTHIQ — Reports Hub Page (FEAT-REPORTS-001)
// ─────────────────────────────────────────────

import { useState } from 'react';
import Link from '@/components/common/AccessLink';
import PageHeader from '@/components/common/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { permissionForPath } from '@klothiq/domain';
import { useDashboardKPIs } from '@/hooks/useDashboard';
import {
  TrendingUp,
  ShoppingBag,
  Warehouse,
  Clock,
  ClipboardList,
  RotateCcw,
  ArrowRight,
  Calendar,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export type ReportTimeframe = '7d' | '30d' | '6m' | '1y';

interface ReportCardDef {
  id: string;
  category: string;
  type: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  accentClass: string;
  iconBg: string;
  iconColor: string;
  roles?: ('owner' | 'admin' | 'staff' | 'viewer')[];
  getTeaser: (kpis: any) => { label: string; value: string };
}

const REPORT_CARDS: ReportCardDef[] = [
  {
    id: 'sales-summary',
    category: 'sales',
    type: 'summary',
    title: 'Sales Summary',
    description: 'Revenue, profit and current collected balances grouped by sale date.',
    icon: <TrendingUp className="h-5 w-5" />,
    accentClass: 'border-l-4 border-l-cyan-500',
    iconBg: 'bg-cyan-500/10',
    iconColor: 'text-cyan-500',
    getTeaser: (kpis) => ({
      label: 'This Month Sales',
      value: kpis?.sales_this_month
        ? `₹${Number(kpis.sales_this_month).toLocaleString('en-IN')}`
        : '₹0',
    }),
  },
  {
    id: 'sales-by-product',
    category: 'sales',
    type: 'by-product',
    title: 'Sales by Product / Team',
    description: 'Contribution analysis by product, team, and top-selling variants.',
    icon: <ShoppingBag className="h-5 w-5" />,
    accentClass: 'border-l-4 border-l-emerald-500',
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-500',
    getTeaser: () => ({
      label: 'Drill-down',
      value: 'By Team / Category',
    }),
  },
  {
    id: 'inventory-valuation',
    category: 'inventory',
    type: 'valuation',
    title: 'Stock Valuation & Low Stock',
    description: 'Current capital tied up in inventory, weighted average costs, and low-stock reorder thresholds.',
    icon: <Warehouse className="h-5 w-5" />,
    accentClass: 'border-l-4 border-l-blue-500',
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-500',
    getTeaser: (kpis) => ({
      label: 'Stock on Hand',
      value: kpis?.total_stock_on_hand
        ? `${kpis.total_stock_on_hand} units`
        : '0 units',
    }),
  },
  {
    id: 'receivables-aging',
    category: 'financial',
    type: 'receivables-aging',
    title: 'Receivables Aging',
    description: 'Overdue customer balances partitioned into 0-30d, 31-60d, 61-90d, and 90d+ risk buckets.',
    icon: <Clock className="h-5 w-5" />,
    accentClass: 'border-l-4 border-l-amber-500',
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-500',
    roles: ['owner', 'admin'],
    getTeaser: (kpis) => ({
      label: 'Total Outstanding',
      value: kpis?.total_receivable
        ? `₹${Number(kpis.total_receivable).toLocaleString('en-IN')}`
        : '₹0',
    }),
  },
  {
    id: 'purchases-summary',
    category: 'purchases',
    type: 'summary',
    title: 'Purchases & Payables',
    description: 'Procurement volume, supplier spending breakdowns, and outstanding payment obligations.',
    icon: <ClipboardList className="h-5 w-5" />,
    accentClass: 'border-l-4 border-l-purple-500',
    iconBg: 'bg-purple-500/10',
    iconColor: 'text-purple-500',
    roles: ['owner', 'admin'],
    getTeaser: () => ({
      label: 'Procurement',
      value: 'Supplier Ledgers',
    }),
  },
  {
    id: 'returns-summary',
    category: 'returns',
    type: 'summary',
    title: 'Returns Summary',
    description: 'Return request rates, acceptance metrics, warehouse restock verification, and refund status.',
    icon: <RotateCcw className="h-5 w-5" />,
    accentClass: 'border-l-4 border-l-rose-500',
    iconBg: 'bg-rose-500/10',
    iconColor: 'text-rose-500',
    getTeaser: () => ({
      label: 'Lifecycle Tracking',
      value: 'Requests & Restocks',
    }),
  },
];

const TIMEFRAME_OPTIONS: { id: ReportTimeframe; label: string }[] = [
  { id: '7d', label: 'Last 7 Days' },
  { id: '30d', label: 'Last 30 Days' },
  { id: '6m', label: 'Last 6 Months' },
  { id: '1y', label: 'This Fiscal Year' },
];

export default function ReportsHubPage() {
  const { profile, can } = useAuth();
  const userRole = (profile?.role as 'owner' | 'admin' | 'staff' | 'viewer') ?? 'staff';
  const [timeframe, setTimeframe] = useState<ReportTimeframe>('6m');
  const { data: kpis } = useDashboardKPIs();

  return (
    <div className="space-y-6">
      {/* ── Page Header & Global Controls ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader
          title="Reports & Analytics"
          description="Operational intelligence, inventory valuation, and financial summaries"
        />

        {/* Global Date Range Selector */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <div className="flex items-center gap-1.5 rounded-lg border border-border/80 bg-card px-2.5 py-1.5 text-xs text-muted-foreground shadow-xs">
            <Calendar className="h-3.5 w-3.5 text-primary" />
            <span className="font-medium text-foreground">Timeframe:</span>
            <div className="flex items-center gap-1 ml-1">
              {TIMEFRAME_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setTimeframe(opt.id)}
                  className={cn(
                    'rounded-md px-2 py-0.5 text-[11px] font-semibold transition-all',
                    timeframe === opt.id
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'hover:bg-muted/60 text-muted-foreground'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {can('sales_contribution.view_all')&&<Link to="/team-sales" className="block rounded-xl border border-primary/30 bg-primary/5 p-5"><h2 className="font-semibold">Team Sales</h2><p className="mt-1 text-sm text-muted-foreground">Seller credit, order entry activity and payment detail by person.</p></Link>}
      {/* ── Category Cards Grid ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {REPORT_CARDS.map((card) => {
          const isRestricted = (card.roles && !card.roles.includes(userRole)) || !can(permissionForPath(`/reports/${card.category}/${card.type}`)!);
          if (isRestricted) return null;
          const teaser = kpis ? card.getTeaser(kpis) : { label: 'Report', value: 'Open report' };
          const targetUrl = `/reports/${card.category}/${card.type}?range=${timeframe}`;



          return (
            <Link
              key={card.id}
              to={targetUrl}
              className={cn(
                'glass-card group relative flex flex-col justify-between p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-primary/40',
                card.accentClass
              )}
            >
              <div>
                <div className="flex items-center justify-between">
                  <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl transition-transform group-hover:scale-105', card.iconBg, card.iconColor)}>
                    {card.icon}
                  </div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground/80 bg-muted/40 px-2 py-0.5 rounded-md">
                    {card.category}
                  </span>
                </div>

                <h3 className="mt-4 text-base font-bold text-foreground group-hover:text-primary transition-colors">
                  {card.title}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                  {card.description}
                </p>
              </div>

              <div className="mt-6 pt-3 border-t border-border/50 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{teaser.label}</p>
                  <p className="text-xs font-bold text-foreground mt-0.5">{teaser.value}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary transition-transform group-hover:translate-x-1">
                  View Report <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
